import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";

/**
 * Lazily loads a user's profile picture via /files/{path} and renders it as an <img>.
 * Falls back to `fallback` (initial or icon) when there's no path or the fetch fails.
 * Blob URLs are cached in a module-level Map so the same picture is only downloaded
 * once per session even when it appears in many chat rows.
 */
const cache = new Map(); // path -> blobUrl
const inflight = new Map(); // path -> Promise<blobUrl>

export async function fetchPicture(path) {
  if (cache.has(path)) return cache.get(path);
  if (inflight.has(path)) return inflight.get(path);
  const p = (async () => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      cache.set(path, url);
      return url;
    } catch (err) {
      logDev(err);
      return null;
    } finally {
      inflight.delete(path);
    }
  })();
  inflight.set(path, p);
  return p;
}

export default function UserAvatar({ path, fallback, className = "", size = 32, onClick, testId }) {
  const [url, setUrl] = useState(() => (path ? cache.get(path) || null : null));

  useEffect(() => {
    let cancelled = false;
    if (!path) { setUrl(null); return; }
    if (cache.has(path)) { setUrl(cache.get(path)); return; }
    fetchPicture(path).then((u) => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, [path]);

  const style = { width: size, height: size };
  const commonProps = {
    "data-testid": testId,
    onClick,
    style,
    className: `rounded-full grid place-items-center shrink-0 overflow-hidden ${onClick ? "cursor-pointer hover:opacity-80" : ""} ${className}`,
  };
  if (url) {
    return (
      <div {...commonProps}>
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  return <div {...commonProps}>{fallback}</div>;
}
